import React, { SyntheticEvent, useEffect, useMemo, useRef } from 'react';

import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from '@mui/material';
import TextField from '@mui/material/TextField';

import { useGetTypicalMonthsQuery } from 'state/apiSlice';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import {
  DayTypeSetting,
  TimePeriod,
  selectAnalysisEndDate,
  selectAnalysisIsSaving,
  selectAnalysisName,
  selectAnalysisStartDate,
  selectDayTypeSetting,
  selectIncludeMtr,
  selectODUIMode,
  selectTimePeriod,
  setAnalysisEndDate,
  setAnalysisName,
  setAnalysisStartDate,
  setDayTypeSetting,
  setIncludeMtr,
  setTimePeriod,
  useGetAnalysesQuery,
} from 'state/originDestinationSlices';

import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import StlHelperTxt from 'features/common/StlHelperTxt';
import StlSidebarFormWrapper from 'features/common/StlSidebarForm/StlSidebarForm';
import { ToggleGroup } from 'features/common/ToggleGroup';
import {
  dateToString,
  displayTypicalMonth,
  parseMonthYear,
} from 'features/common/datetimeUtils';
import { getIncrementalName, updateNameError } from 'features/common/utils';
import { BaseMonthPicker } from 'features/task_bar/TypicalMonthPicker';
import { formatDateShort } from 'features/user_admin/common';

import { ODUIMode } from '../../../appConstants';
import {
  selectProject,
  selectTypicalMonth,
} from '../../../state/workflowSlice';
import { ODLabel } from '../ODLabel';
import AnalysisCreateButtons from './AnalysisCreateButtons';
import ODDateRangePicker from './ODDateRangePicker';

import styles from './ODSidebar.module.css';

const LABEL_MAP = { [TimePeriod.MONTH]: 'Month', [TimePeriod.RANGE]: 'Range' };

export function AnalysisSettings() {
  const dispatch = useAppDispatch();

  const analysisName = useAppSelector(selectAnalysisName);
  const project_slug = useAppSelector(selectProject);
  const uiMode = useAppSelector(selectODUIMode);
  const analysisIsSaving = useAppSelector(selectAnalysisIsSaving);
  const typicalMonth = useAppSelector(selectTypicalMonth);
  const analysisStart = useAppSelector(selectAnalysisStartDate);
  const analysisEnd = useAppSelector(selectAnalysisEndDate);
  const dayTypeSetting = useAppSelector(selectDayTypeSetting);
  const includeMtr = useAppSelector(selectIncludeMtr);
  const timePeriod = useAppSelector(selectTimePeriod);

  const { currentData: analyses } = useGetAnalysesQuery(project_slug, {
    skip: !project_slug,
  });
  const { data: typicalMonthsInfo } = useGetTypicalMonthsQuery(project_slug, {
    skip: !project_slug,
  });
  const selectedTypicalMonth = useMemo(
    () => typicalMonthsInfo?.find((m) => m.typicalMonth === typicalMonth),
    [typicalMonthsInfo, typicalMonth],
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const editable = uiMode === ODUIMode.Create;
  const disabled = !editable || analysisIsSaving;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isFocused = inputRef.current === document.activeElement;
    if (!isFocused) return;
    dispatch(setAnalysisName(e.target.value));
    editedNameRef.current = true;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') e.currentTarget.blur();
  };

  const existingNames = useMemo(
    () => (analyses ?? []).map((s) => s.name.trim().toLowerCase()),
    [analyses],
  );

  const autoName: string = useMemo(() => {
    let name: string;
    if (timePeriod === TimePeriod.MONTH && typicalMonth) {
      name = typicalMonth.trim();
    } else if (analysisStart || analysisEnd) {
      name = `${formatDateShort(analysisStart)} - ${analysisEnd ? formatDateShort(analysisEnd) : ''}`;
    } else {
      name = '';
    }
    while (existingNames.includes(name.toLowerCase())) {
      name = getIncrementalName(name);
    }
    return name;
  }, [analysisEnd, analysisStart, timePeriod, typicalMonth, existingNames]);

  const editedNameRef = useRef(analysisName && analysisName !== autoName);

  const error = updateNameError(
    undefined,
    analysisName || '',
    existingNames,
    editedNameRef.current,
  );

  // update auto name
  useEffect(() => {
    if (!editedNameRef.current && !analysisIsSaving)
      dispatch(setAnalysisName(autoName));
  }, [autoName, analysisIsSaving]);

  /** apply a date from a month picker */
  useEffect(() => {
    if (timePeriod === TimePeriod.MONTH && typicalMonth) {
      const monthStart = parseMonthYear(typicalMonth);
      const monthEnd = monthStart.endOf('month');
      dispatch(setAnalysisStartDate(dateToString(monthStart)));
      dispatch(setAnalysisEndDate(dateToString(monthEnd)));
    }
  }, [typicalMonth, timePeriod]);

  const handleDayTypeChange = (_: SyntheticEvent, checked: boolean) => {
    dispatch(
      setDayTypeSetting(
        checked ? DayTypeSetting.BY_DAY_TYPE : DayTypeSetting.COMBINED,
      ),
    );
  };

  const handleIncludeMtrChange = (_: SyntheticEvent, checked: boolean) => {
    dispatch(setIncludeMtr(checked));
  };

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    dispatch(setAnalysisEndDate(undefined));
    dispatch(setAnalysisStartDate(undefined));

    const val = event.currentTarget.value as TimePeriod;
    dispatch(setTimePeriod(val));
    if (val === TimePeriod.MONTH)
      dispatch(setDayTypeSetting(DayTypeSetting.BY_DAY_TYPE));
    else dispatch(setDayTypeSetting(DayTypeSetting.COMBINED));
  };

  return (
    <StlSidebarFormWrapper
      btns={
        editable && (
          <AnalysisCreateButtons
            disableRun={Boolean(error) || disabled}
            disableClear={disabled}
          />
        )
      }
    >
      <div>
        <ODLabel lbl="Name" />
        <TextField
          inputRef={inputRef}
          value={analysisName ?? ''}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          size="small"
          error={Boolean(error)}
          helperText={
            error ? <StlHelperTxt variant="error" txt={error} /> : null
          }
          InputProps={{
            classes: {
              input: styles.nameInput,
            },
          }}
        />
      </div>
      <div>
        <ODLabel lbl="Time Period" />
        <ToggleGroup
          handleClick={handleToggle}
          value={timePeriod}
          values={Object.values(TimePeriod)}
          horizontal
          labelMap={LABEL_MAP}
        />
      </div>
      <div>
        {timePeriod === TimePeriod.MONTH && (
          <Stack display="flex" gap={0.5}>
            <BaseMonthPicker
              classes={{ menuItem: styles.fS14, paper: styles.mL8 }}
              fullWidth
              querier={useGetTypicalMonthsQuery}
              menuItemLabel={(info) =>
                info.usesVolumes
                  ? displayTypicalMonth(info.typicalMonth)
                  : `${displayTypicalMonth(info.typicalMonth)} (proportions)`
              }
            />
            {selectedTypicalMonth && !selectedTypicalMonth.usesVolumes && (
              <VolumesNotAvailable />
            )}
          </Stack>
        )}
        {timePeriod === TimePeriod.RANGE && (
          <Stack display="flex" gap={0.5}>
            <ODDateRangePicker />
            <VolumesNotAvailable text="for date ranges" />
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    size="medium"
                    checked={dayTypeSetting === DayTypeSetting.BY_DAY_TYPE}
                    onChange={handleDayTypeChange}
                  />
                }
                label={
                  <Typography variant="body2" align="left">
                    Split metrics by weekday/weekend days
                  </Typography>
                }
              />
            </FormGroup>
          </Stack>
        )}
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="medium"
                checked={includeMtr}
                onChange={handleIncludeMtrChange}
              />
            }
            label={
              <Typography variant="body2">
                Include Most Traveled Route
              </Typography>
            }
          />
        </FormGroup>
      </div>
    </StlSidebarFormWrapper>
  );
}

function VolumesNotAvailable({ text }: { text?: string }) {
  return (
    <Stack direction="row" gap={0.5}>
      <FontAwesomeIcon name="circle-info" type="regular" />
      <Typography variant="body1" textAlign="left">
        Volumes are not available {text}
      </Typography>
    </Stack>
  );
}
